// AuthPrimitive — wraps supabase.auth, emits AuthSignedIn/Out events on the bus.
// Option A signup (D-08) closes AUTH-01: handle + display_name forwarded as
// raw_user_meta_data so the Plan 03 on_auth_user_created trigger persists the
// full profile chain (public.authors + public.users + public.channels +
// public.channel_writers) in one round-trip.
import type { SupabaseClient, User } from "@supabase/supabase-js"
import { useSyncExternalStore } from "react"
import type { EventBus } from "./eventBus"

export class AuthSignedInEvent {
  readonly userId: string
  readonly email: string
  constructor(userId: string, email: string) {
    this.userId = userId
    this.email = email
  }
}
export class AuthSignedOutEvent {
  readonly userId: string
  constructor(userId: string) {
    this.userId = userId
  }
}

export interface SignUpArgs {
  email: string
  password: string
  /** Lowercase URL-slug-safe handle (D-08, regex `^[a-z0-9_-]{3,32}$`). Sent as raw_user_meta_data.handle. */
  handle: string
  /** Free-form display name (1..50 chars). Sent as raw_user_meta_data.display_name. */
  display_name: string
}

export class AuthPrimitive {
  #user: User | null = null
  #listeners = new Set<() => void>()
  readonly #supabase: SupabaseClient
  readonly #bus: EventBus

  constructor(supabase: SupabaseClient, bus: EventBus) {
    this.#supabase = supabase
    this.#bus = bus
  }

  async start(): Promise<void> {
    const { data } = await this.#supabase.auth.getSession()
    this.#user = data.session?.user ?? null
    this.#notify()
    if (this.#user) {
      await this.#bus.emit(new AuthSignedInEvent(this.#user.id, this.#user.email ?? ""))
    }

    this.#supabase.auth.onAuthStateChange(async (_event, session) => {
      const prev = this.#user
      this.#user = session?.user ?? null
      this.#notify()
      if (this.#user && !prev) {
        await this.#bus.emit(new AuthSignedInEvent(this.#user.id, this.#user.email ?? ""))
      } else if (!this.#user && prev) {
        await this.#bus.emit(new AuthSignedOutEvent(prev.id))
      }
    })
  }

  /**
   * Option A signup (closes the AUTH-01 pipeline gap).
   * Forwards { handle, display_name } as raw_user_meta_data so the Plan 03
   * `on_auth_user_created` trigger can persist public.authors + public.users
   * in a single round-trip. Server-side trigger lowercases + regex-validates
   * the handle; UNIQUE constraint surfaces duplicate handle as an auth error.
   */
  signUp(args: SignUpArgs) {
    return this.#supabase.auth.signUp({
      email: args.email,
      password: args.password,
      options: {
        data: {
          handle: args.handle.toLowerCase(),
          display_name: args.display_name,
        },
      },
    })
  }

  signIn(args: { email: string; password: string }) {
    return this.#supabase.auth.signInWithPassword(args)
  }

  signOut() {
    return this.#supabase.auth.signOut()
  }

  sendPasswordReset(email: string) {
    return this.#supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback`,
    })
  }

  useUser(): User | null {
    return useSyncExternalStore(
      (cb) => {
        this.#listeners.add(cb)
        return () => {
          this.#listeners.delete(cb)
        }
      },
      () => this.#user,
      () => null,
    )
  }

  useEmailConfirmed(): boolean {
    const u = this.useUser()
    return u?.email_confirmed_at != null
  }

  #notify(): void {
    for (const cb of this.#listeners) cb()
  }
}
